import { useContext } from 'react';
import {
    MenuItem, Grid, Button, FormControlLabel, Table, TableBody, TableCell, TableHead, TableRow,
    Typography, Link, Tooltip, IconButton, RadioGroup, Radio, List, ListItem
} from '@mui/material';
import { ErrorMessage, Field, FieldProps, FormikProps } from 'formik';
import { TextField } from 'formik-mui';
import { applicationContext, appUserContext } from 'helpers/services';
import { Autocomplete, CustomQuillEditor, MachineAutocomplete, MessageStatus } from 'components';
import { newTicketContext } from '../NewTicketContext';
import { IMachineSearch } from 'components/MachineAutocomplete/MachineAutocomplete';
import { MdDelete } from 'react-icons/md';
import { IoPersonAddOutline as AddPersonIcon, IoBusinessSharp as SimecUserIcon, IoPersonOutline as CustomerUserIcon } from 'react-icons/io5';
import { localeManagerContext } from 'helpers/Locale';
import NewReporter from './NewReporter';
import Moment from 'react-moment';
import { useIntl } from 'react-intl';
import { LocalizationIds } from 'helpers/Localizations';
import { UserTypology } from 'helpers/Enums';

interface IProps {
    formProps: FormikProps<any>
}

let NewTicketForm = ({ formProps }: IProps) => {
    let { formatMessage } = useIntl();

    let appService = useContext(applicationContext);
    let service = useContext(newTicketContext);
    let { currentLocale } = useContext(localeManagerContext);
    let { currentUser, isMobile } = useContext(appUserContext);


    let machineAutocompleteHandler = (value: IMachineSearch) => {
        if (value.linkedTo === "Order")
            service.orderSelectionHandler({ ...value.id });

        if (value.linkedTo === "ProductionOrder")
            service.workOrderSelectionHandler({ ...value.id });

        if (formProps.values.customerId === 0) {
            service.customerIdChangeHandler(value.customerId || 0);
            formProps.setFieldValue('customerId', value.customerId || 0)
            formProps.setFieldValue('customerName', value.customerName || '')
        }

    }


    return (
        <>
            <Grid container>
                <Grid item xs={12} md={6}>
                    <Field
                        select
                        component={TextField}
                        label={formatMessage({ id: LocalizationIds.Category })}
                        name="ticketSubCategoryId"
                    >
                        {appService.ticketSubCategories.map(subcategory =>
                            <MenuItem key={subcategory.id} value={subcategory.id}>
                                {subcategory.categoryNames[currentLocale]} / {subcategory.names[currentLocale]}
                            </MenuItem>
                        )}
                    </Field>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Field
                        component={TextField}
                        select
                        label={formatMessage({ id: LocalizationIds.Priority })}
                        name="priority"
                    >
                        {appService.ticketPriorities.map(priority =>
                            <MenuItem key={priority.key} value={priority.key}>{priority.value}</MenuItem>
                        )}
                    </Field>
                </Grid>
                <Grid item xs={12} md={12}>
                    <Field
                        component={TextField}
                        label={formatMessage({ id: LocalizationIds.Title })}
                        name="title"
                    />
                </Grid>

                {service.fromMessage !== true &&
                    <>
                        <Grid item xs={12} md={12} style={{ height: 186 }}>
                            <Field name="message">
                                {({ field }: FieldProps) =>
                                    <div data-text-editor="name">
                                        <CustomQuillEditor
                                            style={{ height: 136 }}
                                            value={field.value}
                                            onChange={field.onChange(field.name)}
                                        />
                                    </div>
                                }
                            </Field>
                        </Grid>

                        <Grid item xs md>
                            <ErrorMessage name="message">
                                {msg => <Typography variant="caption" style={{ color: '#f44336' }}>{msg}</Typography>}
                            </ErrorMessage>
                        </Grid>
                        <Grid item>
                            <MessageStatus
                                isPublic={formProps.values.customerCanView}
                                source={UserTypology.Simec}
                            />
                        </Grid>

                        <Grid item xs={12} md={12}>
                            <Autocomplete
                                inputLabel={formatMessage({ id: LocalizationIds.Customer })}
                                value={{
                                    value: service.customerId,
                                    label: formProps.values.customerName
                                }}
                                doSearch={(value: string, etag: number) => service.searchCustomers(value, etag)}
                                onChange={(value: any) => {
                                    service.customerIdChangeHandler(value?.value || 0)
                                    formProps.setFieldValue('customerName', value?.customerName || '')
                                    formProps.setFieldValue('customerCountryCode', value?.countryCode || '')
                                    formProps.setFieldValue('customerCountryName', value?.countryName || '')
                                }}
                            />
                            <ErrorMessage name="customerId">
                                {msg => <Typography variant="caption" style={{ color: '#f44336' }}>{msg}</Typography>}
                            </ErrorMessage>
                        </Grid>
                    </>
                }
                <Grid item xs={12} md={12}>
                    <MachineAutocomplete
                        onChange={(value) => machineAutocompleteHandler(value)}
                        inputLabel={formatMessage({ id: LocalizationIds.SearchMachineExtended })}
                        customerId={service.customerId === 0 ? undefined : service.customerId}
                        linkedTo={service.selectedOrders.length !== 0 ? 'Order' : (service.selectedWorkOrders.length !== 0 ? 'ProductionOrder' : undefined)}
                    />
                </Grid>

                {(service.selectedOrders.length !== 0 || service.selectedWorkOrders.length !== 0) &&
                    <Grid item xs={12} md={12}>
                        {service.selectedOrders.length !== 0 &&
                            <>
                                {isMobile === false &&
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>{formatMessage({ id: LocalizationIds.Number })}</TableCell>
                                                <TableCell>{formatMessage({ id: LocalizationIds.Title })}</TableCell>
                                                <TableCell align="right"></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {service.selectedOrders.map((order, index) =>
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        <Link target="_blank" href={`${process.env.REACT_APP_CRM_URL}/spa/Order/${order.orderId}`}>
                                                            {order.orderType}{order.orderNumber}
                                                        </Link>
                                                    </TableCell>
                                                    <TableCell>{order.orderTitle}</TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title={formatMessage({ id: LocalizationIds.Delete })}>
                                                            <IconButton
                                                                size="small"
                                                                color="secondary"
                                                                onClick={() => service.orderSelectionHandler(order)}
                                                            >
                                                                <MdDelete />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                }
                                {isMobile === true &&
                                    <List>
                                        {service.selectedOrders.map((order, index) =>
                                            <ListItem disableGutters divider key={index}>
                                                <Grid container>
                                                    <Grid item xs={12}>
                                                        <Typography variant="body2">
                                                            <b>{formatMessage({ id: LocalizationIds.Number })}:</b>&nbsp;
                                                        </Typography>
                                                        <Link target="_blank" href={`${process.env.REACT_APP_CRM_URL}/spa/Order/${order.orderId}`}>
                                                            {order.orderType}{order.orderNumber}
                                                        </Link>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <Typography variant="body2">
                                                            <b>{formatMessage({ id: LocalizationIds.Title })}:</b>&nbsp;
                                                        </Typography>
                                                        {order.orderTitle}
                                                    </Grid>
                                                    <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center' }}>
                                                        <Button color="secondary" onClick={(e: any) => {
                                                            e.stopPropagation();
                                                            service.orderSelectionHandler(order);
                                                        }}>
                                                            <MdDelete /> {formatMessage({ id: LocalizationIds.Delete })}
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            </ListItem>
                                        )}
                                    </List>
                                }
                            </>
                        }
                        {service.selectedWorkOrders.length !== 0 &&
                            <>
                                {isMobile === false &&
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>{formatMessage({ id: LocalizationIds.WorkOrder })}</TableCell>
                                                <TableCell>{formatMessage({ id: LocalizationIds.Order })}</TableCell>
                                                <TableCell>{formatMessage({ id: LocalizationIds.Description })}</TableCell>
                                                <TableCell align="right"></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {service.selectedWorkOrders.map((workOrder, index) =>
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        <Link target="_blank" href={`${process.env.REACT_APP_CRM_URL}/spa/WorkOrder/${workOrder.workOrderId}`}>
                                                            {workOrder.workOrderNumber}
                                                        </Link>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Link target="_blank" href={`${process.env.REACT_APP_CRM_URL}/spa/Order/${workOrder.orderId}`}>
                                                            <Typography variant="body2">
                                                                {workOrder.orderType}{workOrder.orderNumber}
                                                            </Typography>
                                                        </Link>
                                                        {workOrder.orderDate &&
                                                            <Typography variant="caption">
                                                                <Moment date={workOrder.orderDate} format="LL" />
                                                            </Typography>
                                                        }
                                                    </TableCell>
                                                    <TableCell>{workOrder.articleId}&nbsp;{workOrder.articleModel}&nbsp;{workOrder.articleVersion}&nbsp;{workOrder.articleDescription}</TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title={formatMessage({ id: LocalizationIds.Delete })}>
                                                            <IconButton
                                                                size="small"
                                                                color="secondary"
                                                                onClick={() => service.workOrderSelectionHandler({ ...workOrder })}
                                                            >
                                                                <MdDelete />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                }
                                {isMobile === true &&
                                    <List>
                                        {service.selectedWorkOrders.map((workOrder, index) =>
                                            <ListItem disableGutters divider key={index}>
                                                <Grid container>
                                                    <Grid item xs={12}>
                                                        <Typography variant="body2">
                                                            <b>{formatMessage({ id: LocalizationIds.WorkOrder })}:</b>&nbsp;
                                                        </Typography>
                                                        <Link target="_blank" href={`${process.env.REACT_APP_CRM_URL}/spa/WorkOrder/${workOrder.workOrderId}`}>
                                                            {workOrder.workOrderNumber}
                                                        </Link>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <Typography variant="body2">
                                                            <b>{formatMessage({ id: LocalizationIds.Order })}:</b>&nbsp;
                                                        </Typography>
                                                        <Link target="_blank" href={`${process.env.REACT_APP_CRM_URL}/spa/Order/${workOrder.orderId}`}>
                                                            <Typography variant="body2">
                                                                {workOrder.orderType}{workOrder.orderNumber}
                                                            </Typography>
                                                        </Link>
                                                        {workOrder.orderDate &&
                                                            <Typography variant="caption">
                                                                <Moment date={workOrder.orderDate} format="LL" />
                                                            </Typography>
                                                        }
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <Typography variant="body2">
                                                            <b>{formatMessage({ id: LocalizationIds.Description })}:</b>&nbsp;
                                                        </Typography>
                                                        {workOrder.articleId}&nbsp;{workOrder.articleModel}&nbsp;{workOrder.articleVersion}&nbsp;{workOrder.articleDescription}
                                                    </Grid>
                                                    <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center' }}>
                                                        <Button color="secondary" onClick={(e: any) => {
                                                            e.stopPropagation();
                                                            service.workOrderSelectionHandler({ ...workOrder });
                                                        }}>
                                                            <MdDelete /> {formatMessage({ id: LocalizationIds.Delete })}
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            </ListItem>
                                        )}
                                    </List>
                                }
                            </>
                        }
                    </Grid>
                }

                <Grid item xs={12} md={12}>
                    {(service.customerId !== 0 || service.fromMessage === true) &&
                        <>
                            <Field
                                component={TextField}
                                select
                                label={formatMessage({ id: LocalizationIds.Reporter })}
                                name="reporterUserName"
                                onChange={(event: any) => {
                                    formProps.setFieldValue('reporterUserName', event.target.value || '')
                                    if (event.target.value === 'addnew') {
                                        formProps.setFieldValue('addNewReporter', true);
                                        formProps.setFieldValue('reporterIsExternal', true);
                                    } else {
                                        formProps.setFieldValue('addNewReporter', false);
                                        formProps.setFieldValue('newReporterUserName', '');
                                        let reporterIsExternal = service.reporters
                                            .find(f => f.value === event.target.value)?.isExternal ?? true;
                                        formProps.setFieldValue('reporterIsExternal', reporterIsExternal);
                                    }
                                }}
                            >
                                {currentUser?.simecClaims?.canManageCustomerUsers === true &&
                                    <MenuItem value={'addnew'}><AddPersonIcon />&nbsp;{formatMessage({ id: LocalizationIds.AddNew })}...</MenuItem>
                                }
                                {service.reporters.map(reporter =>
                                    <MenuItem key={reporter.value} value={reporter.value}>
                                        {reporter.isExternal === false && <SimecUserIcon />}
                                        {reporter.isExternal === true && <CustomerUserIcon />}
                                        &nbsp;{reporter.label}
                                    </MenuItem>
                                )}
                            </Field>
                        </>
                    }
                    {service.customerId === 0 && service.fromMessage !== true &&
                        <Typography variant="caption">{formatMessage({ id: LocalizationIds.SelectCustomerOrMachineToSetReporter })}</Typography>
                    }
                </Grid>
                {formProps.values.reporterUserName === 'addnew' &&
                    <NewReporter />
                }
                {(formProps.values.reporterIsExternal && appService.ticketSubCategories.getValueByKey(formProps.values.ticketSubCategoryId, 'id', 'canBeSelectedByCustomer') === true) &&
                    <Grid item xs={12} md={12}>
                        <RadioGroup
                            row
                            value={formProps.values.customerCanView}
                            onChange={(e: any) => {
                                formProps.setFieldValue('customerCanView', ('' + e.target.value) === 'true')
                            }}
                        >
                            <FormControlLabel value={true} control={<Radio />} label={formatMessage({ id: LocalizationIds.SharedWithCustomer })} />
                            <FormControlLabel value={false} control={<Radio />} label={formatMessage({ id: LocalizationIds.SimecPrivateTicket })} />
                        </RadioGroup>
                    </Grid>
                }
            </Grid>

            <Grid container>
                <Grid item xs />
                <Grid item>
                    <Button type="submit" variant="contained">
                        {formatMessage({ id: LocalizationIds.CreateTicket })}
                    </Button>
                </Grid>
            </Grid>
        </>
    );
}

export default NewTicketForm;